import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { DollarSign, Tag } from "lucide-react";

export default function PricingSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  const activeRoutes = team?.active_routes || [];
  const routeFares = decision.route_fares || {};

  const ROUTES_INFO = {
    R01: { name: "Hub - City A", avg: 90 }, R02: { name: "Hub - City B", avg: 100 },
    R03: { name: "Hub - City C", avg: 160 }, R04: { name: "Hub - City D", avg: 180 },
    R05: { name: "Hub - City E", avg: 200 }, R06: { name: "Hub - City F", avg: 450 },
    R07: { name: "Hub - City G", avg: 550 }, R08: { name: "City A - City C", avg: 130 },
  };

  const updateFare = (routeId, fareType, value) => {
    const fares = { ...routeFares };
    if (!fares[routeId]) fares[routeId] = { economy: ROUTES_INFO[routeId]?.avg || 100, business: 0 };
    fares[routeId] = { ...fares[routeId], [fareType]: parseFloat(value) || 0 };
    updateDecision("route_fares", fares);
  };

  const routeConfig = team?.route_config || {};
  const hasBusiness = (routeId) => {
    const config = routeConfig[routeId] || {};
    return config.class_config === "economy_business" || config.class_config === "full_three_class";
  };

  return (
    <div className="space-y-6">
      {/* Route Fares */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> {t("decisions.routeFares")}</CardTitle>
          <CardDescription>{t("decisions.routeFaresDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {activeRoutes.map(routeId => {
            const info = ROUTES_INFO[routeId] || { name: routeId, avg: 100 };
            const fares = routeFares[routeId] || { economy: info.avg, business: 0 };
            const showBiz = hasBusiness(routeId);
            return (
              <div key={routeId} className="p-4 border border-slate-200 rounded-lg bg-slate-50" data-testid={`pricing-route-${routeId}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-slate-800">{routeId}: {info.name}</span>
                  <span className="text-xs text-slate-500">Market avg: {formatCurrency(info.avg)}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">{t("decisions.economyFare")} ($50-$800)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <Input type="number" min={50} max={800} step={5} value={fares.economy || info.avg} onChange={e => updateFare(routeId, "economy", e.target.value)} className="pl-7 font-mono h-9" data-testid={`economy-fare-${routeId}`} />
                    </div>
                    <FareIndicator fare={fares.economy || info.avg} marketAvg={info.avg} />
                  </div>
                  {showBiz && (
                    <div>
                      <Label className="text-xs">{t("decisions.businessFare")} ($200-$3000)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <Input type="number" min={200} max={3000} step={10} value={fares.business || info.avg * 2.5} onChange={e => updateFare(routeId, "business", e.target.value)} className="pl-7 font-mono h-9" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {activeRoutes.length === 0 && <p className="text-slate-500 text-sm text-center py-4">{t("decisions.noActiveRoutes")}</p>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fare Strategy & Discount */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Tag className="w-5 h-5" /> {t("decisions.fareStrategyLabel")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <div>
              <Label className="text-xs">{t("decisions.fareStrategyLabel")}</Label>
              <Select value={decision.fare_strategy || "fixed"} onValueChange={v => updateDecision("fare_strategy", v)}>
                <SelectTrigger className="mt-1" data-testid="fare-strategy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">{t("decisions.fixedPricing")}</SelectItem>
                  <SelectItem value="dynamic">{t("decisions.dynamicPricing")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex justify-between"><Label className="text-xs">{t("decisions.discountLevel")}</Label><span className="text-xs font-mono text-sky-600 font-semibold">{decision.discount_level || 0}%</span></div>
              <Slider value={[decision.discount_level || 0]} onValueChange={([v]) => updateDecision("discount_level", v)} min={0} max={40} step={5} className="mt-2" data-testid="discount-slider" />
              <p className="text-[10px] text-slate-400 mt-1">Higher discount = higher load factor, lower revenue per seat</p>
            </div>
            <div>
              <Label className="text-xs">{t("decisions.targetSegment")}</Label>
              <Select value={decision.target_segment || "mixed"} onValueChange={v => updateDecision("target_segment", v)}>
                <SelectTrigger className="mt-1" data-testid="target-segment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leisure">{t("decisions.leisure")}</SelectItem>
                  <SelectItem value="business">{t("decisions.business")}</SelectItem>
                  <SelectItem value="mixed">{t("decisions.mixed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Ancillary Revenue */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.ancillaryRevenue")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <Label className="text-xs">{t("decisions.ancillaryPackage")}</Label>
            <div className="space-y-2 mt-2">
              {[
                { value: "none", label: "None", desc: "No ancillary revenue", revenue: "$0/pax" },
                { value: "basic", label: "Basic", desc: "Baggage fees, seat selection", revenue: "+$8/pax" },
                { value: "premium", label: "Premium", desc: "Full ancillary suite (baggage, meals, priority)", revenue: "+$18/pax" },
              ].map(opt => (
                <button key={opt.value} onClick={() => updateDecision("ancillary_package", opt.value)} className={`w-full p-3 text-left border-2 rounded-lg transition-colors ${decision.ancillary_package === opt.value ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`ancillary-${opt.value}`}>
                  <div className="flex justify-between">
                    <span className="font-medium text-sm">{opt.label}</span>
                    <span className="text-xs font-mono text-sky-600">{opt.revenue}</span>
                  </div>
                  <p className="text-xs text-slate-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FareIndicator({ fare, marketAvg }) {
  const ratio = fare / marketAvg;
  let color = "text-slate-500";
  let label = "At market";
  if (ratio < 0.7) { color = "text-emerald-600"; label = "30%+ below (high load, low yield)"; }
  else if (ratio < 0.9) { color = "text-emerald-500"; label = "10-30% below (good load)"; }
  else if (ratio > 1.3) { color = "text-rose-600"; label = "30%+ above (low load, high yield)"; }
  else if (ratio > 1.1) { color = "text-amber-600"; label = "10-30% above (lower load)"; }
  return <p className={`text-[10px] mt-1 ${color}`}>{label}</p>;
}
